import * as _ from 'lodash';

import { API_WEBHOOK_NOTIFICATIONS, ARCHIVE_AND_LICENSE_UPLOAD, BINARY_DECOMPOSITION, PACKAGE_INVENTORY, RAW_VULN_EDIT } from '../../../../shared/featureFlags/flagNames';
import { window } from '../../../globals';
import { isFeatureEnabled } from '../../../helpers/features';
import { userHasPermissionForPolicyType } from '../../../helpers/permissions';
import { Action } from '../../../shared/permissions';
import { PolicyType } from '../../../types';
import { shouldDisplayAuditLogs, shouldDisplayAuthenticationSettings, shouldDisplayBillingSettings, shouldDisplayCustomLicenseScanSettings, shouldDisplayJiraSettings, shouldDisplayLanguageSettings, shouldDisplayNotificationBanners, shouldDisplayOrganizationGeneralSettings, shouldDisplayOrganizationPortalSettings, shouldDisplayProjectGeneralSettings, shouldDisplayProjectIssueSettings, shouldDisplayProjectNotificationSettings, shouldDisplayProjectSettings, shouldDisplayProjectUpdateHookSettings, shouldDisplaySlackSettings, shouldDisplayUserSettings } from '../../settings/shared/permissions';
import type { CommandPaletteSearchFEResult } from '../../../../shared/CommandPalette/types';

export type CommandPaletteRoutesMap = Map<string, CommandPaletteSearchFEResult>;

export const ALL_ROUTES: CommandPaletteRoutesMap = new Map<string, CommandPaletteSearchFEResult>(
  Object.entries<CommandPaletteSearchFEResult>({
    projects: {
      to: '/projects',
      label: 'Projects',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    releaseGroups: {
      to: '/release-groups',
      label: 'Release Groups',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    issues: {
      to: '/issues',
      label: 'Issues',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'issues.overview': {
      to: '/issues/overview',
      label: 'Issues › Overview',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'issues.licensing': {
      to: '/issues/licensing',
      label: 'Issues › Licensing',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'issues.vulnerability': {
      to: '/issues/vulnerability',
      label: 'Issues › Vulnerability',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    packages: {
      to: '/packages',
      label: 'Packages',
      type: 'staticPage',
      shouldEnable: () => isFeatureEnabled(PACKAGE_INVENTORY),
    },
    reports: {
      to: '/reports',
      label: 'Reports',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    policies: {
      to: '/policies',
      label: 'Policies',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'policies.licensing': {
      to: '/policies/licensing',
      label: 'Policies › Licensing',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'policies.security': {
      to: '/policies/security',
      label: 'Policies › Security',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'policies.quality': {
      to: '/policies/quality',
      label: 'Policies › Quality',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'policies.sbom': {
      to: '/policies/sbom',
      label: 'Policies › SBOM',
      type: 'staticPage',
      shouldEnable: () => userHasPermissionForPolicyType(Action.View, PolicyType.SBOM),
    },
    settings: {
      to: '/account/settings',
      label: 'Account Settings',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'settings.organization': {
      to: '/account/settings/organization',
      label: 'Settings › Organization',
      type: 'staticPage',
      shouldEnable: () => shouldDisplayOrganizationGeneralSettings(),
    },
    'settings.organization.portal': {
      to: '/account/settings/organization/portal',
      label: 'Settings › Organization › Portal',
      type: 'staticPage',
      shouldEnable: () => shouldDisplayOrganizationPortalSettings(),
    },
    'settings.organization.teams': {
      to: '/account/settings/organization/teams',
      label: 'Settings › Organization › Teams',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'settings.organization.roles': {
      to: '/account/settings/organization/roles',
      label: 'Settings › Organization › Roles',
      type: 'staticPage',
      shouldEnable: () => shouldDisplayUserSettings(),
    },
    'settings.organization.users': {
      to: '/account/settings/organization/users',
      label: 'Settings › Organization › Users',
      type: 'staticPage',
      shouldEnable: () => shouldDisplayUserSettings(),
    },
    'settings.organization.banners': {
      to: '/account/settings/organization/banners',
      label: 'Settings › Organization › Banners',
      type: 'staticPage',
      shouldEnable: () => shouldDisplayNotificationBanners(),
    },
    'settings.projects': {
      to: '/account/settings/projects',
      label: 'Settings › Projects',
      type: 'staticPage',
      shouldEnable: () => shouldDisplayProjectSettings(),
    },
    'settings.organization.authentication': {
      to: '/account/settings/organization/authentication',
      label: 'Settings › Organization › Authentication',
      type: 'staticPage',
      shouldEnable: () => shouldDisplayAuthenticationSettings(),
    },
    'settings.projects.general': {
      to: '/account/settings/projects/general',
      label: 'Settings › Projects › General',
      type: 'staticPage',
      shouldEnable: () => shouldDisplayProjectGeneralSettings(),
    },
    'settings.projects.issues': {
      to: '/account/settings/projects/issues',
      label: 'Settings › Projects › Issues',
      type: 'staticPage',
      shouldEnable: () => shouldDisplayProjectIssueSettings(),
    },
    'settings.projects.notifications': {
      to: '/account/settings/projects/notifications',
      label: 'Settings › Projects › Notifications',
      type: 'staticPage',
      shouldEnable: () => shouldDisplayProjectNotificationSettings(),
    },
    'settings.projects.hooks': {
      to: '/account/settings/projects/hooks',
      label: 'Settings › Projects › Hooks',
      type: 'staticPage',
      shouldEnable: () => shouldDisplayProjectUpdateHookSettings(),
    },
    'settings.integrations': {
      to: '/account/settings/integrations',
      label: 'Settings › Integrations',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'settings.integrations.jira': {
      to: '/account/settings/integrations/jira',
      label: 'Settings › Integrations › Jira',
      type: 'staticPage',
      shouldEnable: () => shouldDisplayJiraSettings(),
    },
    'settings.integrations.github': {
      to: '/account/settings/integrations/github',
      label: 'Settings › Integrations › GitHub',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'settings.integrations.slack': {
      to: '/account/settings/integrations/slack',
      label: 'Settings › Integrations › Slack',
      type: 'staticPage',
      shouldEnable: () => shouldDisplaySlackSettings(),
    },
    'settings.integrations.bitbucket': {
      to: '/account/settings/integrations/bitbucket',
      label: 'Settings › Integrations › Bitbucket',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'settings.integrations.gitlab': {
      to: '/account/settings/integrations/gitlab',
      label: 'Settings › Integrations › GitLab',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'settings.integrations.api_tokens': {
      to: '/account/settings/integrations/api_tokens',
      label: 'Settings › Integrations › API Tokens',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'settings.integrations.custom_license_scans': {
      to: '/account/settings/integrations/custom_license_scans',
      label: 'Settings › Integrations › Custom License Scans',
      type: 'staticPage',
      shouldEnable: () => shouldDisplayCustomLicenseScanSettings(),
    },
    'settings.integrations.api_webhooks': {
      to: '/account/settings/integrations/api_webhooks',
      label: 'Settings › Integrations › API Webhooks',
      type: 'staticPage',
      shouldEnable: () => isFeatureEnabled(API_WEBHOOK_NOTIFICATIONS),
    },
    'settings.languages': {
      to: '/account/settings/languages',
      label: 'Settings › Languages',
      type: 'staticPage',
      shouldEnable: () => shouldDisplayLanguageSettings(),
    },
    'settings.languages.java': {
      to: '/account/settings/languages/java',
      label: 'Settings › Languages › Java',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'settings.languages.npm': {
      to: '/account/settings/languages/npm',
      label: 'Settings › Languages › NPM',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'settings.languages.bower': {
      to: '/account/settings/languages/bower',
      label: 'Settings › Languages › Bower',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'settings.languages.cocoa': {
      to: '/account/settings/languages/cocoa',
      label: 'Settings › Languages › Cocoa',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'settings.languages.ruby': {
      to: '/account/settings/languages/ruby',
      label: 'Settings › Languages › Ruby',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'settings.languages.python': {
      to: '/account/settings/languages/python',
      label: 'Settings › Languages › Python',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'settings.languages.dotnet': {
      to: '/account/settings/languages/dotnet',
      label: 'Settings › Languages › .NET',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'settings.languages.git': {
      to: '/account/settings/languages/git',
      label: 'Settings › Languages › Git',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'settings.billing': {
      to: '/account/settings/billing',
      label: 'Settings › Billing',
      type: 'staticPage',
      shouldEnable: () => !window.on_prem && shouldDisplayBillingSettings(),
    },
    'settings.auditLogs': {
      to: '/account/settings/audit-logs',
      label: 'Settings › Audit Logs',
      type: 'staticPage',
      shouldEnable: () => shouldDisplayAuditLogs(),
    },
    'projects.import.cli': {
      to: '/projects/import/cli',
      label: 'Projects › Import › CLI',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'projects.import.cloud': {
      to: '/projects/import/cloud',
      label: 'Projects › Import › Cloud',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'projects.import.github-app': {
      to: '/projects/import/github-app',
      label: 'Projects › Import › GitHub App',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'projects.import.github': {
      to: '/projects/import/github',
      label: 'Projects › Import › GitHub',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'projects.import.bitbucket_cloud': {
      to: '/projects/import/bitbucket_cloud',
      label: 'Projects › Import › Bitbucket Cloud',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'projects.import.gitlab': {
      to: '/projects/import/gitlab',
      label: 'Projects › Import › GitLab',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'projects.import.azure_repos': {
      to: '/projects/import/azure_repos',
      label: 'Projects › Import › Azure Repos',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    'projects.import.upload.archive': {
      to: '/projects/import/upload/archive',
      label: 'Projects › Import › Upload › Archive',
      type: 'staticPage',
      shouldEnable: () => isFeatureEnabled(ARCHIVE_AND_LICENSE_UPLOAD),
    },
    'projects.import.upload.binary': {
      to: '/projects/import/upload/binary',
      label: 'Projects › Import › Upload › Binary',
      type: 'staticPage',
      shouldEnable: () => isFeatureEnabled(BINARY_DECOMPOSITION),
    },
    'projects.import.manual': {
      to: '/projects/import/manual',
      label: 'Projects › Import › Manual',
      type: 'staticPage',
      shouldEnable: _.constant(true),
    },
    admin: {
      to: '/admin',
      label: 'Admin',
      type: 'staticPage',
      shouldEnable: () => !!window.user?.super,
    },
    internalTools: {
      to: '/internal-tools',
      label: 'Internal Tools',
      type: 'staticPage',
      shouldEnable: () => !!window.user?.super,
    },
    rawVulns: {
      to: '/raw-vulns',
      label: 'Raw Vulns',
      type: 'staticPage',
      shouldEnable: () => isFeatureEnabled(RAW_VULN_EDIT),
    },
  }),
);
